/*
 © Copyright 2026 Daikin Industries, Ltd. All rights reserved.

 This software is the proprietary information of Daikin Industries, Ltd.
 By using this software, you agree to the following terms:

 1. You are granted a revocable non-exclusive, non-transferable license to use this software.
 2. You agree not to modify, distribute, or create derivative works based on this software.
 3. Unauthorized copying, distribution, or use of any portion of this software is strictly prohibited.
 4. This software is protected by copyright laws and international treaties.
 5. Any violation of these terms will result in the termination of your license and may result in legal action.
 6. This is Daikin's proprietary software and may not be used in an unauthorized manner or without express authorization from Daikin.
*/

/**
 * Swagger UI CSS Loader Utility
 *
 * Handles dynamic loading of Swagger UI CSS with CDN fallback
 */

export class SwaggerCssLoader {
  private static readonly CSS_IDS = {
    LOCAL: 'swagger-ui-css',
    CDN: 'swagger-ui-css-cdn'
  } as const;

  private static readonly CDN_URL = 'https://unpkg.com/swagger-ui-dist@4.15.5/swagger-ui.css';
  private static readonly LOCAL_URL = '/swagger-ui.css';

  /**
   * Loads Swagger UI CSS with automatic CDN fallback
   * @returns Promise that resolves when CSS is loaded
   */
  static async load(): Promise<void> {
    // Check if CSS is already loaded
    if (document.getElementById(this.CSS_IDS.LOCAL) || document.getElementById(this.CSS_IDS.CDN)) {
      return Promise.resolve();
    }

    return new Promise<void>((resolve, reject) => {
      const link = this.createLinkElement(this.CSS_IDS.LOCAL, this.LOCAL_URL);

      link.onload = () => {
        console.log('✅ Swagger UI CSS loaded successfully from local');
        resolve();
      };

      link.onerror = () => {
        console.warn('⚠️ Failed to load local swagger-ui.css, trying CDN fallback');
        this.loadFromCDN()
          .then(resolve)
          .catch(reject);
      };

      document.head.appendChild(link);
    });
  }

  /**
   * Loads Swagger UI CSS from CDN as fallback
   */
  private static loadFromCDN(): Promise<void> {
    // Check if CDN CSS is already loaded
    if (document.getElementById(this.CSS_IDS.CDN)) {
      return Promise.resolve();
    }

    return new Promise<void>((resolve, reject) => {
      const link = this.createLinkElement(this.CSS_IDS.CDN, this.CDN_URL);

      link.onload = () => {
        console.log('✅ Swagger UI CSS loaded from CDN');
        resolve();
      };

      link.onerror = () => {
        const error = new Error('Failed to load Swagger UI CSS from both local and CDN sources');
        console.error('❌', error.message);
        reject(error);
      };

      document.head.appendChild(link);
    });
  }

  /**
   * Creates a link element for CSS loading
   */
  private static createLinkElement(id: string, href: string): HTMLLinkElement {
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.type = 'text/css';
    link.href = href;
    return link;
  }
}
